import { Injectable } from '@angular/core';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { combineLatest, iif, Observable, of } from 'rxjs';

import { UserKeys } from '@config/keys/user.keys';
import { MetafieldKeys } from '@config/keys/metafield.keys';
import { DistributionModeValues } from '@config/values/order.values';

import { PongoApiService, PongoOrderingContext } from '@core/services/api/pongo/pongo-api.service';

import { DoodUserModel } from '@store/authentication/authentication.model';
import { DoodProductModel } from '@core/models/product.model';
import { IPongoAsset, IPongoCustomer } from '@core/models/pongo.model';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { DoodShopModel } from '@core/models/shop.model';

@Injectable({
  providedIn: 'root',
})
export class PongoService {
  /**
   * Set when Pongo create/link is opened without a real account, so login/signup
   * can reopen this modal instead of leaving the user on the shop page.
   */
  private reopenCreateOrLinkAfterAuth = false;

  constructor(
    private authSelector: AuthStoreSelector,
    private readonly pongoApiService: PongoApiService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private shopSelector: ShopStoreSelector,
    private readonly settingsStoreSelector: SettingsStoreSelector,
  ) {}

  /**
   * Same ordering context as Zerosix. Null is forwarded so the API layer can
   * omit the field and keep existing web payloads unchanged.
   * Kiosk calls assume the backend accepts an anonymous user (DOOD-3399).
   */
  private readonly orderingContext$: Observable<PongoOrderingContext> = combineLatest({
    kiosk: this.settingsStoreSelector.selectKiosk,
    parameters: this.settingsStoreSelector.selectParameters$,
  }).pipe(
    map(({ kiosk, parameters }) => {
      if (kiosk.isKioskLayout) {
        return 'kiosk';
      }
      if (parameters?.distribution_mode === DistributionModeValues.ClickAndCollect) {
        return 'click_and_collect';
      }
      return null;
    }),
  );

  rememberCreateOrLinkAfterAuth(): void {
    this.reopenCreateOrLinkAfterAuth = true;
  }

  consumeCreateOrLinkAfterAuth(): boolean {
    const pending = this.reopenCreateOrLinkAfterAuth;
    this.reopenCreateOrLinkAfterAuth = false;
    return pending;
  }

  shouldOpenCreateOrLinkModal(
    user: DoodUserModel | null | undefined,
    isPluginEnabled: boolean,
    isAnonymous: boolean,
  ): boolean {
    if (!isPluginEnabled || isAnonymous) {
      this.reopenCreateOrLinkAfterAuth = false;
      return false;
    }

    return this.consumeCreateOrLinkAfterAuth() || !this.hasPongoCustomer(user);
  }

  hasPongoCustomer(user?: DoodUserModel | null): boolean {
    const marketplaceId = this.marketplaceSelector.selectMarketplaceId();
    if (!user || !marketplaceId || !Array.isArray(user[UserKeys.Metafields])) {
      return false;
    }

    return !!user[UserKeys.Metafields]!.find(
      metafield =>
        metafield[MetafieldKeys.Handle] === `pongo_customer_id_marketplace_${marketplaceId}`,
    );
  }

  isUserPongoCustomerEnabledOnMarketplaceLevel$(): Observable<boolean> {
    return combineLatest([
      this.authSelector.selectUser$,
      this.marketplaceSelector.selectMarketplaceId$,
    ]).pipe(
      take(1),
      map(([user]) => this.hasPongoCustomer(user)),
    );
  }

  getMarketplaceRewards$(): Observable<IPongoAsset[]> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      switchMap(marketplaceId => this.pongoApiService.getMarketplaceRewards$(marketplaceId)),
    );
  }

  createCustomer$(phoneNumber: string, hasOptin: Boolean): Observable<DoodUserModel> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, orderingContext]) =>
        this.pongoApiService.createCustomer$(marketplaceId, phoneNumber, hasOptin, orderingContext),
      ),
    );
  }

  getCustomer$(shop?: DoodShopModel | null): Observable<IPongoCustomer | undefined> {
    return this.isUserPongoCustomerEnabledOnMarketplaceLevel$().pipe(
      switchMap(pongoEnabled =>
        iif(
          () => pongoEnabled,
          this.marketplaceSelector.selectMarketplaceId$.pipe(
            take(1),
            switchMap(marketplaceId => this.pongoApiService.getCustomer$(marketplaceId, shop?.id)),
          ),
          of(undefined),
        ),
      ),
    );
  }

  attachCustomer$(phoneNumber: string): Observable<DoodUserModel> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, orderingContext]) =>
        this.pongoApiService.attachCustomer$(marketplaceId, phoneNumber, orderingContext),
      ),
    );
  }

  buyReward$(rewardId: string): Observable<IPongoAsset> {
    return combineLatest([
      this.marketplaceSelector.selectMarketplaceId$,
      this.shopSelector.selectActive$,
    ]).pipe(
      take(1),
      switchMap(([marketplaceId, shop]) => {
        return this.pongoApiService.buyReward$(marketplaceId, rewardId, shop?.id);
      }),
    );
  }

  allowedProducts$(externalIds: string[], shopId?: string): Observable<DoodProductModel[]> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      switchMap(marketplaceId =>
        this.pongoApiService.allowedProducts$(marketplaceId, externalIds, shopId),
      ),
    );
  }
}
